import posthog from "posthog-js"
import { getCookieConsent, setCookieConsent, isGlobalPrivacyControlEnabled, isUSRegion } from "../utils/cookie"

let posthogService = null

const loadConfig = () => ({
  apiKey: process.env.REACT_APP_POSTHOG_KEY,
  host: process.env.REACT_APP_POSTHOG_HOST,
  enabled: process.env.REACT_APP_POSTHOG_ENABLED === "true",
  environment: process.env.REACT_APP_ENVIRONMENT,
  platform: process.env.REACT_APP_POSTHOG_PLATFORM,
  consentRequired: process.env.REACT_APP_POSTHOG_CONSENT_REQUIRED === "true",
})

export class AnalyticsService {
  constructor() {
    this.config = loadConfig()
    this.lastResetTime = 0
    this.resetDebounceMs = 1000 // Prevent resets within 1 second of each other
    this.posthog = null
    this.initialized = false
    this.subscribers = new Set()
    this.pendingUser = null
    this.pendingProperties = null
    this.userIdentified = false
    // Cache snapshot to prevent infinite re-renders with useSyncExternalStore
    this._cachedSnapshot = this._computeSnapshot()
  }

  // Arrow functions for useSyncExternalStore compatibility
  subscribe = callback => {
    this.subscribers.add(callback)
    return () => this.subscribers.delete(callback)
  }

  // Return cached snapshot - must be same object reference if values haven't changed
  getSnapshot = () => this._cachedSnapshot

  _computeSnapshot() {
    return {
      status: this._getStatus(),
      isOptedIn: this._isOptedIn(),
      isSetup: this._isSetup(),
    }
  }

  _updateSnapshot() {
    const newSnapshot = this._computeSnapshot()
    // Only update if values actually changed
    if (
      newSnapshot.status !== this._cachedSnapshot.status ||
      newSnapshot.isOptedIn !== this._cachedSnapshot.isOptedIn ||
      newSnapshot.isSetup !== this._cachedSnapshot.isSetup
    ) {
      this._cachedSnapshot = newSnapshot
    }
  }

  _getStatus() {
    // GPC always takes precedence over stored consent (CPRA compliance)
    if (isGlobalPrivacyControlEnabled()) {
      return "rejected"
    }

    const consent = getCookieConsent()
    if (consent) {
      return consent // "accepted" or "rejected"
    }

    return "pending"
  }

  _isOptedIn() {
    const status = this._getStatus()

    // Always respect explicit rejection, regardless of region (CCPA/CPRA requires honoring opt-out)
    if (status === "rejected") {
      return false
    }

    // US users are auto-opted-in when no preference exists (implied consent is legal under CCPA)
    // GPC already handled by _getStatus() returning "rejected" above
    if (isUSRegion() && status === "pending") {
      return true
    }

    if (!this.config.consentRequired) {
      return true
    }

    return status === "accepted"
  }

  _isSetup() {
    return this.config.enabled && this.config.apiKey && this.posthog != null
  }

  _notifySubscribers() {
    this._updateSnapshot()
    this.subscribers.forEach(cb => cb())
  }

  _flushPending() {
    if (!this._isSetup() || !this._isOptedIn()) {
      return
    }

    if (this.pendingUser) {
      const { userId, properties } = this.pendingUser
      try {
        this.posthog.identify(userId, this._buildProperties(properties))
        this.pendingUser = null
        this.userIdentified = true
        console.info("👤 PostHog: User identified")
      } catch (error) {
        console.error("Failed to flush pending user:", error)
      }
    }

    if (this.pendingProperties && this.userIdentified) {
      try {
        // Properties are associated with the currently identified user automatically
        this.posthog.setPersonProperties(this._buildProperties(this.pendingProperties))
        this.pendingProperties = null
        console.info("👤 PostHog: User properties set")
      } catch (error) {
        console.error("Failed to flush pending properties:", error)
      }
    }
  }

  /**
   * Reset PostHog session and clear pending user data.
   *
   * @param {Object} [options={}] - Reset options
   * @param {boolean} [options.debounce=true] - Whether to apply debounce protection.
   *   Set to false for logout where reset must always execute.
   */
  reset({ debounce = true } = {}) {
    // Clear pending data on logout/reset
    this.pendingUser = null
    this.pendingProperties = null
    this.userIdentified = false

    const now = Date.now()

    // Prevent multiple resets within the debounce window (unless debounce disabled)
    if (debounce && now - this.lastResetTime < this.resetDebounceMs) {
      console.info("⏭️ Skipping duplicate reset (too soon after last reset)")
      return
    }

    // Always update timestamp when we actually reset
    this.lastResetTime = now

    if (!this._isSetup()) {
      // Debug mode - log the reset
      console.debug("🔍 PostHog Debug: reset (not initialized)")
      return
    }

    try {
      this.posthog.reset()
      console.info(debounce ? "🔄 PostHog: Session reset" : "🧹 PostHog: User data cleared")
    } catch (error) {
      console.error("Failed to reset PostHog:", error)
    }
  }

  _flushIfReady() {
    if (!this._isSetup() || !this._isOptedIn()) {
      return
    }
    this._flushPending()
  }

  setUser(userId, properties) {
    this.pendingUser = { userId, properties }
    this._flushIfReady()
  }

  setUserProperties(properties) {
    this.pendingProperties = properties
    this._flushIfReady()
  }

  isFeatureEnabled(flagKey, defaultValue) {
    if (!this._isSetup()) {
      console.debug(`🔍 PostHog Debug Feature Flag: ${flagKey} -> ${defaultValue} (default)`)
      return defaultValue
    }

    try {
      return this.posthog.isFeatureEnabled(flagKey) ?? defaultValue
    } catch (error) {
      console.error(`Failed to check PostHog feature flag: ${flagKey}`, error)
      return defaultValue
    }
  }

  getFeaturePayload(flagKey) {
    if (!this._isSetup()) {
      console.debug(`🔍 PostHog Debug Feature Payload: ${flagKey} -> {} (default)`)
      return {}
    }

    try {
      return this.posthog.getFeatureFlagPayload(flagKey)
    } catch (error) {
      console.error(`Failed to get PostHog feature payload ${flagKey}:`, error)
      return {}
    }
  }

  /**
   * Opt in to analytics tracking.
   * Called when user accepts cookies.
   */
  optIn() {
    setCookieConsent("accepted")

    if (this._isSetup()) {
      try {
        // Enable persistence and session recording (autocapture/pageview always off - we control explicitly)
        this.posthog.set_config({ disable_persistence: false })
        this.posthog.startSessionRecording()
        console.info("🎬 PostHog: Tracking enabled on opt-in")
      } catch (error) {
        console.error("Failed to enable PostHog features on opt-in:", error)
      }
    }

    this._notifySubscribers()
    this._flushPending()
  }

  /**
   * Opt out of analytics tracking.
   * Called when user rejects cookies or revokes consent.
   */
  optOut() {
    setCookieConsent("rejected")

    if (this._isSetup()) {
      try {
        this.posthog.stopSessionRecording()
        // Disable persistence before reset to prevent new cookies from being created
        // (autocapture/pageview always off - we control explicitly)
        this.posthog.set_config({ disable_persistence: true })
        console.info("🚫 PostHog: Tracking disabled on opt-out")
      } catch (error) {
        console.error("Failed to disable PostHog features on opt-out:", error)
      }
    }

    // Clear pending user data and reset PostHog session
    this.reset({ debounce: false })
    this._notifySubscribers()
  }

  /**
   * Clear all user data and reset PostHog session.
   * Call this BEFORE logout redirect to prevent identity leak.
   * Alias for reset({ debounce: false }).
   */
  clear() {
    this.reset({ debounce: false })
  }

  /**
   * Capture a custom analytics event with PostHog.
   * Automatically adds timestamp and environment to properties.
   *
   * @param {string} eventName - The name of the event to capture (e.g., "button_click", "page_view")
   * @param {Object} [properties={}] - Additional event properties to include
   */
  capture(eventName, properties) {
    if (!this._isSetup() || !this._isOptedIn()) {
      console.debug(`🔍 PostHog Debug Event: ${eventName}`)
      return
    }

    try {
      const fullProperties = this._buildProperties(properties)
      this.posthog.capture(eventName, fullProperties)
      console.info(`📊 PostHog event captured: ${eventName}`)
    } catch (error) {
      console.error(`Failed to capture PostHog event: ${eventName}`, error)
    }
  }

  /**
   * Track a page view event with PostHog analytics.
   * Wraps the capture() method with page_view event type.
   *
   * @param {string} pageName - The human-readable page name (e.g., "Dashboard", "Map")
   * @param {Object} [properties={}] - Additional properties to include (e.g., page_path, referrer)
   */
  pageView(pageName, properties) {
    if (!this._isSetup() || !this._isOptedIn()) {
      console.debug(`🔍 PostHog Debug PageView: ${pageName}`)
      return
    }

    const pageProperties = {
      page: pageName,
      ...properties,
    }
    this.capture("page_view", pageProperties)
  }

  _buildProperties(properties) {
    return {
      timestamp: new Date().toISOString(),
      environment: this.config.environment,
      platform: this.config.platform,
      ...properties,
    }
  }
}

const initialize = () => {
  const service = new AnalyticsService()

  // Always initialize PostHog immediately if enabled (for feature flags)
  if (service.config.enabled && service.config.apiKey) {
    // Determine if we need to restrict tracking based on consent state
    const consent = getCookieConsent()
    const gpcEnabled = isGlobalPrivacyControlEnabled()
    const usRegion = isUSRegion()

    // Always respect explicit rejection, regardless of region (CCPA/CPRA requires honoring opt-out)
    // US users get full tracking when no preference exists (implied consent)
    // Non-US users need explicit consent when consentRequired is true
    const needsRestriction = gpcEnabled ||
                             consent === "rejected" ||
                             (service.config.consentRequired &&
                              !usRegion &&
                              consent !== "accepted")

    posthog.init(service.config.apiKey, {
      api_host: service.config.host,
      // Always disable auto-tracking - we control events explicitly via capture()/pageView()
      capture_pageview: false,
      autocapture: false,
      // Session recording and persistence depend on consent state
      disable_session_recording: needsRestriction,
      disable_persistence: needsRestriction,
      loaded: _posthog => {
        service.posthog = _posthog
        service.initialized = true
        console.info(`🚀 PostHog initialized for ${service.config.environment} environment (restricted: ${needsRestriction})`)
        service._notifySubscribers()
        service._flushIfReady()
      },
    })
  }

  return service
}

export const getAnalyticsService = () => {
  if (posthogService != null) {
    return posthogService
  }

  posthogService = initialize()

  return posthogService
}
